import { cn } from '@/lib/utils';
import { AudioLines, Languages, MapPin } from 'lucide-react';
import type { JSX } from 'react';
import { NavLink, Outlet, useOutletContext } from 'react-router-dom';

interface HealthSubTab {
  label: string;
  /** Path segment relative to the Health tab base. */
  path: string;
  Icon: typeof MapPin;
}

interface HealthTabShellProps {
  /** Absolute base path for the Health tab (e.g. `/tours/abc/health`). */
  basePath: string;
  /** Heading shown above the sub-nav, e.g. "Tour health" or "Greater area health". */
  title: string;
  description: string;
}

const SUB_TABS: HealthSubTab[] = [
  { label: 'Location', path: 'location', Icon: MapPin },
  { label: 'Descriptions & Voiceovers', path: 'content', Icon: AudioLines },
  { label: 'Translations', path: 'translations', Icon: Languages },
];

/**
 * Generic shell for the Health tab on Tour and Greater Area detail pages.
 * Renders a small sub-nav for the health checks (currently just Location)
 * and an Outlet for whichever check the user picked. Designed to grow —
 * adding "Translations", "Voiceovers", "Photos", etc. is a one-line append
 * to SUB_TABS plus a child route.
 */
export function HealthTabShell({ basePath, title, description }: HealthTabShellProps): JSX.Element {
  // Forward whatever outlet context the parent route handed us so deeply
  // nested sub-tabs (e.g. AttractionHealthLocationTab) can read the
  // entity from useAttractionContext / useTourContext without triggering
  // a duplicate query that races against EntityDetailShell's own fetch.
  const parentContext = useOutletContext<unknown>();

  return (
    <div className="space-y-4 px-4 pb-4">
      <header>
        <h2 className="text-lg font-medium tracking-tight text-zinc-900">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </header>

      <nav
        role="tablist"
        aria-label="Health checks"
        className="flex flex-wrap gap-1 border-b"
      >
        {SUB_TABS.map(({ label, path, Icon }) => (
          <NavLink
            key={path}
            to={`${basePath}/${path}`}
            role="tab"
            className={({ isActive }) =>
              cn(
                'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div>
        <Outlet context={parentContext} />
      </div>
    </div>
  );
}
